import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { Newspaper, TrendingUp, Clock, ExternalLink, Shield, Plus, Sparkles, Globe, X } from "lucide-react";
import { fetchNewsThunk } from "../../redux/news/newsActionThunks";
import { selectNewsFeed } from "../../redux/news/newsSlice";
import { fetchCredibilityThunk } from "../../redux/credibility/credibilityActionThunks";
import { selectCredByUser } from "../../redux/credibility/credibilitySlice";
import { fetchWatchlistThunk } from "../../redux/user/watchlist/watchlistActions";
import { fetchGroups } from "../../redux/userGroups/groups/userGroupies";
import { searchGlobalAPI } from "../../redux/searchBar/searchAPI";
import StockLogo from "../../components/StockLogo/StockLogoRender";
import { tierColors, PUBLISHING_ELIGIBLE_TIERS } from "../../utils/credibilityTiers";

// Same watchlist-∪-my-groups relevance pattern GroupListPage.jsx's own
// `relevantGroups` already established (member_user_id match, not `m.id`,
// which is the membership row's own PK -- a documented past bug) -- reused
// here rather than reinvented.
function usePersonalizedTickers() {
  const { items: watchlistItems = [] } = useSelector((s) => s.watchlist || {});
  const { groups = [] } = useSelector((s) => s.groups || {});
  const { user: currentUser } = useSelector((s) => s.auth || {});

  return useMemo(() => {
    const symbols = new Set();
    watchlistItems.forEach((w) => w?.symbol && symbols.add(w.symbol.toUpperCase()));

    const myGroups = groups.filter((g) => {
      const isOwner = g?.created_by === currentUser?.id || g?.createdBy?.id === currentUser?.id;
      const isMember = Array.isArray(g?.members) && g.members.some((m) => m.user_id === currentUser?.id);
      return isOwner || isMember;
    });
    myGroups.forEach((g) => {
      (Array.isArray(g?.stocks) ? g.stocks : []).forEach((s) => s?.symbol && symbols.add(s.symbol.toUpperCase()));
      (Array.isArray(g?.tickers) ? g.tickers : []).forEach((t) => t && symbols.add(String(t).toUpperCase()));
    });

    return Array.from(symbols);
  }, [watchlistItems, groups, currentUser]);
}

const ArticleCard = ({ article }) => {
  const { t } = useTranslation();
  const isElite = article.source_type === "elite_original";
  const to = isElite ? `/news/${article.id}` : article.article_url;
  const external = !isElite;
  const colors = isElite ? tierColors(article.author_tier) : null;

  const CardInner = (
    <div
      className={`relative rounded-2xl p-5 transition-all duration-300 ${
        isElite
          ? "bg-gray-50 dark:bg-gradient-to-br dark:from-white/[0.06] dark:to-white/[0.02] border hover:border-violet-400/40"
          : "bg-gray-50 border border-gray-200 hover:border-gray-300 dark:bg-white/[0.03] dark:border-white/[0.06] dark:hover:border-white/[0.12]"
      }`}
      style={isElite ? { borderColor: "rgba(167,139,250,0.25)" } : undefined}
    >
      {isElite && (
        <div className="absolute -top-2.5 left-4 flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-lg shadow-violet-900/40">
          <Sparkles size={10} /> {t('news.verifiedOriginal')}
        </div>
      )}

      <div className="flex items-start justify-between gap-3 mb-2 mt-1">
        <h3 className="text-gray-900 dark:text-white font-semibold text-lg leading-snug line-clamp-2">{article.title}</h3>
        {external && <ExternalLink className="text-gray-500 flex-shrink-0 mt-1" size={16} />}
      </div>

      <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-4">
        {article.description || article.body_content}
      </p>

      <div className="flex items-center flex-wrap justify-between gap-3">
        {isElite ? (
          <div className={`flex items-center gap-2 px-2.5 py-1 rounded-lg ${colors.bg} border ${colors.border}`}>
            <Shield size={13} className={colors.text} />
            <span className={`text-xs font-semibold ${colors.text}`}>{article.author_username}</span>
            {article.author_tier && (
              <span className={`text-[10px] uppercase tracking-wide ${colors.text} opacity-80`}>· {article.author_tier}</span>
            )}
          </div>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <Globe size={12} />
            {article.publisher_name || article.author || t('news.externalSourceFallback')}
          </span>
        )}

        <div className="flex items-center gap-3 text-xs text-gray-500">
          {article.published_at && (
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {new Date(article.published_at).toLocaleDateString()}
            </span>
          )}
          {article.tickers?.length > 0 && (
            <span className="flex items-center gap-1">
              {article.tickers.slice(0, 3).map((t) => (
                <span key={t} className="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 px-2 py-0.5 rounded-md">
                  {t}
                </span>
              ))}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className="block">
      {CardInner}
    </a>
  ) : (
    <Link to={to} className="block">
      {CardInner}
    </Link>
  );
};

const NewsFeedPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState("forYou"); // "forYou" | "all"
  const [extraTickers, setExtraTickers] = useState([]);
  const [tickerInput, setTickerInput] = useState("");
  const [sourceType, setSourceType] = useState(""); // "" | curated_external | elite_original
  const [sort, setSort] = useState("recency");

  // 2026-08-11: this ticker filter used to be a bare text field with no
  // lookup at all -- typing "AAP" found nothing (vs. the top-nav search
  // bar's substring+fuzzy match), typing a company name found nothing (no
  // name lookup, ticker-only), and a typo'd chip silently produced an
  // empty feed with zero feedback. Wired to the same real backend search
  // (/search-field/) the top-nav bar uses, but called directly here rather
  // than through its shared Redux thunk/slice -- that slice is written for
  // one always-mounted global search bar, and reusing it here would let
  // this page-local dropdown and the persistent header search bar
  // overwrite each other's results.
  const [tickerSuggestions, setTickerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const tickerInputRef = useRef(null);

  const debouncedTickerSearch = useMemo(
    () =>
      debounce(async (query) => {
        if (!query || query.trim().length < 1) {
          setTickerSuggestions([]);
          setSuggestionsLoading(false);
          return;
        }
        try {
          const data = await searchGlobalAPI(query.trim());
          setTickerSuggestions(Array.isArray(data?.stocks) ? data.stocks.slice(0, 6) : []);
        } catch {
          setTickerSuggestions([]);
        } finally {
          setSuggestionsLoading(false);
        }
      }, 250),
    []
  );

  useEffect(() => {
    if (tickerInput.trim()) {
      setSuggestionsLoading(true);
      setShowSuggestions(true);
      debouncedTickerSearch(tickerInput);
    } else {
      setShowSuggestions(false);
      setTickerSuggestions([]);
    }
    return () => debouncedTickerSearch.cancel();
  }, [tickerInput, debouncedTickerSearch]);

  const feed = useSelector(selectNewsFeed);
  const { feedLoading, feedError } = useSelector((s) => s.news || {});
  const { user } = useSelector((s) => s.auth || {});
  const credibility = useSelector((s) => selectCredByUser(s, user?.id));
  const groupsStatus = useSelector((s) => s.groups?.status);
  const watchlistFetched = useSelector((s) => s.watchlist?.fetched);

  const personalizedTickers = usePersonalizedTickers();

  useEffect(() => {
    if (user?.id) dispatch(fetchCredibilityThunk({ userId: user.id, soft: true }));
    // Guarded on `fetched` -- this is the same global watchlist
    // GroupListPage.jsx also fetches; without the guard, revisiting this
    // page redundantly re-fetches a watchlist that's very likely already
    // in the store.
    if (!watchlistFetched) dispatch(fetchWatchlistThunk({ skip: 0, limit: 100 }));
    if (groupsStatus === "idle") dispatch(fetchGroups());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.id, watchlistFetched]);

  const activeTickers = useMemo(() => {
    const base = viewMode === "forYou" ? personalizedTickers : [];
    return Array.from(new Set([...base, ...extraTickers]));
  }, [viewMode, personalizedTickers, extraTickers]);

  const showsEmptyPersonalization = viewMode === "forYou" && activeTickers.length === 0;

  useEffect(() => {
    if (showsEmptyPersonalization) return; // nothing to fetch, empty state explains why
    dispatch(
      fetchNewsThunk({
        tickers: activeTickers.length ? activeTickers.join(",") : undefined,
        sourceType: sourceType || undefined,
        sort,
        limit: 30,
      })
    );
  }, [dispatch, activeTickers, sourceType, sort, showsEmptyPersonalization]);

  const canPublish = PUBLISHING_ELIGIBLE_TIERS.includes(credibility?.status_tier);

  const addTicker = (explicitSymbol) => {
    const sym = (explicitSymbol || tickerInput).trim().toUpperCase();
    if (sym && !activeTickers.includes(sym)) setExtraTickers((t) => [...t, sym]);
    setTickerInput("");
    setShowSuggestions(false);
    setTickerSuggestions([]);
  };

  const removeTicker = (sym) => {
    if (personalizedTickers.includes(sym) && viewMode === "forYou") {
      // Can't remove an auto-personalized ticker without leaving "For You" --
      // switch the user to "All" + the remaining explicit set instead of
      // silently no-op'ing the click.
      setViewMode("all");
      setExtraTickers(activeTickers.filter((t) => t !== sym));
    } else {
      setExtraTickers((t) => t.filter((x) => x !== sym));
    }
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900">
      <div
        className="pointer-events-none"
        style={{
          position: "fixed",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 420,
          background: "radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.14) 0%, transparent 65%)",
        }}
      />

      <div className="relative max-w-5xl mx-auto p-6 pb-20">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 flex items-center gap-3">
              <span
                className="bg-clip-text text-transparent bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-600 dark:from-violet-300 dark:via-indigo-300 dark:to-blue-300"
                style={{ WebkitBackgroundClip: "text" }}
              >
                {t('news.pageTitle')}
              </span>
            </h1>
            <p className="text-gray-600 dark:text-gray-400 max-w-xl">
              {t('news.pageSubtitle')}
            </p>
          </div>

          {/* Hidden entirely for ineligible users, not shown-and-disabled --
              matches this app's established tier-gated UI convention. */}
          {canPublish && (
            <Link
              to="/news/submit"
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-medium py-3 px-6 rounded-xl flex items-center gap-2 transition shadow-lg shadow-violet-900/30 flex-shrink-0"
            >
              <Plus size={20} />
              {t('news.writeArticle')}
            </Link>
          )}
        </div>

        {/* Primary view toggle */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setViewMode("forYou")}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition flex items-center gap-2 ${
              viewMode === "forYou"
                ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-900/30"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200 dark:bg-white/[0.04] dark:text-gray-300 dark:hover:bg-white/[0.08] dark:border-white/[0.06]"
            }`}
          >
            <Sparkles size={14} /> {t('news.forYou')}
          </button>
          <button
            onClick={() => setViewMode("all")}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition flex items-center gap-2 ${
              viewMode === "all"
                ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-900/30"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200 dark:bg-white/[0.04] dark:text-gray-300 dark:hover:bg-white/[0.08] dark:border-white/[0.06]"
            }`}
          >
            <Globe size={14} /> {t('news.allNews')}
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 mb-6 p-3 rounded-2xl bg-gray-50 border border-gray-200 dark:bg-white/[0.03] dark:border-white/[0.06]">
          <div className="flex items-center gap-1 pr-2 mr-1 border-r border-gray-200 dark:border-white/[0.08]">
            <button
              onClick={() => setSort("recency")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                sort === "recency"
                  ? "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              <Clock size={12} /> {t('news.recent')}
            </button>
            <button
              onClick={() => setSort("trending")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                sort === "trending"
                  ? "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              <TrendingUp size={12} /> {t('news.trending')}
            </button>
          </div>

          <div className="flex items-center gap-1 pr-2 mr-1 border-r border-gray-200 dark:border-white/[0.08]">
            {[
              { value: "", label: t('news.sourceFilters.all') },
              { value: "elite_original", label: t('news.sourceFilters.eliteOriginal') },
              { value: "curated_external", label: t('news.sourceFilters.curatedExternal') },
            ].map((opt) => (
              <button
                key={opt.value || "all"}
                onClick={() => setSourceType(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  sourceType === opt.value
                    ? "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="relative flex items-center flex-wrap gap-1.5">
            {activeTickers.map((t) => (
              <span
                key={t}
                className="flex items-center gap-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 text-xs px-2 py-1 rounded-lg"
              >
                <StockLogo symbol={t} size="w-3.5 h-3.5" />
                {t}
                <button onClick={() => removeTicker(t)} className="hover:text-emerald-100">
                  <X size={11} />
                </button>
              </span>
            ))}
            <input
              ref={tickerInputRef}
              type="text"
              value={tickerInput}
              onChange={(e) => setTickerInput(e.target.value)}
              onFocus={() => tickerInput.trim() && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); addTicker(); }
                if (e.key === "Escape") { setShowSuggestions(false); }
              }}
              placeholder={t('news.tickerInputPlaceholder')}
              className="w-20 bg-transparent text-xs text-gray-700 placeholder-gray-400 dark:text-gray-300 dark:placeholder-gray-600 focus:outline-none px-1 py-1"
              autoComplete="off"
            />

            {showSuggestions && tickerInput.trim() && (
              <div className="absolute top-full left-0 mt-1 w-64 max-h-64 overflow-y-auto bg-white border border-gray-200 dark:bg-[#14141f] dark:border-white/[0.08] rounded-xl shadow-2xl z-20 py-1">
                {suggestionsLoading ? (
                  <div className="px-3 py-2 text-xs text-gray-500">{t('news.tickerSearchLoading')}</div>
                ) : tickerSuggestions.length > 0 ? (
                  tickerSuggestions.map((s) => (
                    <button
                      key={s.symbol}
                      type="button"
                      // onMouseDown (not onClick) fires before the input's
                      // onBlur closes this dropdown -- onClick alone would
                      // never register.
                      onMouseDown={(e) => { e.preventDefault(); addTicker(s.symbol); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-white/[0.06] transition"
                    >
                      <StockLogo symbol={s.symbol} size="w-4 h-4" />
                      <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{s.symbol}</span>
                      {s.name && <span className="text-xs text-gray-500 truncate">{s.name}</span>}
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-xs text-gray-500">{t('news.tickerSearchNoResults')}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {showsEmptyPersonalization ? (
          <div className="text-center py-16 rounded-2xl bg-gray-50 border border-gray-200 dark:bg-white/[0.02] dark:border-white/[0.06]">
            <Sparkles className="mx-auto text-violet-400/50 mb-4" size={40} />
            <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">{t('news.emptyForYouTitle')}</p>
            <p className="text-gray-500 text-sm mb-5 max-w-sm mx-auto">
              {t('news.emptyForYouSubtitle')}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/watchlist" className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 text-sm font-medium">
                {t('news.goToWatchlist')}
              </Link>
              <button
                onClick={() => setViewMode("all")}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm font-medium"
              >
                {t('news.viewAllNews')}
              </button>
            </div>
          </div>
        ) : feedLoading && feed.length === 0 ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-50 border border-gray-200 dark:bg-white/[0.03] dark:border-white/[0.06] rounded-2xl p-5 animate-pulse">
                <div className="h-5 bg-gray-200 dark:bg-white/10 rounded w-3/4 mb-3" />
                <div className="space-y-2 mb-4">
                  <div className="h-3.5 bg-gray-100 dark:bg-white/5 rounded w-full" />
                  <div className="h-3.5 bg-gray-100 dark:bg-white/5 rounded w-2/3" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-24" />
                  <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-16" />
                </div>
              </div>
            ))}
          </div>
        ) : feedError ? (
          <div className="text-center py-16 text-gray-600 dark:text-gray-400">{t('news.errorLoadingNews', { error: feedError })}</div>
        ) : feed.length === 0 ? (
          <div className="text-center py-16">
            <Newspaper className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
            <p className="text-gray-600 dark:text-gray-400">{t('news.noArticlesMatch')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {feed.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsFeedPage;
